// V1.5+ Slice 8 — Signal Storm Endgame tuning per docs/02_GAME_DESIGN.md
// §4.1 Layer 8 / §8 / §15 / §16 / §17.
//
// Signal Storm is the FIRST GLOBAL-RULE-CHANGE slice in V1.5+. Slices 1–7
// changed per-archetype kit (forward turrets, Cut Link, Jam Relay, Disrupt
// Uplink, Fortify Relay) — every match's mechanics were identical at every
// match-second. Signal Storm changes the rules during the final 60 seconds
// (`SIGNAL_STORM_START_SEC` … `state.matchLengthSec`) so a stalemate at 7:00
// becomes mechanically unstable. This is the §17 "final-minute drama" lever
// — until this slice, the last 60 s of a match were identical to the
// previous 7 minutes, only the dominance-scoreboard urgency rose.
//
// Three mechanical levers fire when `state.phase === 'signal_storm'`:
//   1. Forward `mini_turret`s of BOTH sides degrade — fire rate ×
//      `SIGNAL_STORM_TURRET_FIRE_RATE_MULT` (0.75) and range ×
//      `SIGNAL_STORM_TURRET_RANGE_MULT` (0.85). The §15 deathball-prevention
//      lever applied universally — defensive turret nests can't carry the
//      last minute alone. Permanent `turret` buildings (Core-ring) are NOT
//      affected; a player who never pushed a turret out of base shouldn't
//      be punished.
//   2. Capture progress accelerates ×`SIGNAL_STORM_CAPTURE_SPEED_MULT`
//      (1.5). The §17 "force decisions, not stalemate" lever — contested
//      relays flip in ~7 s instead of ~10 s, so a single late commit can
//      swing dominance.
//   3. The bot's `pickRelayTarget` bias forces 'pressure_leader' on every
//      squad regardless of surge state. Mirrors the §17 human heuristic:
//      push the leader's relays because dominance is the timer-decided
//      win condition.
//
// Calibration:
//   - `SIGNAL_STORM_TURRET_FIRE_RATE_MULT = 0.75`
//   - `SIGNAL_STORM_TURRET_RANGE_MULT = 0.85`
//     A tier weaker than Disrupt Uplink (0.6 / 0.7) because the storm
//     hits BOTH sides' turrets — Disrupt's tier on a global rule would be
//     too punishing to forward investment ("if I built a forward turret,
//     the storm just halves its value"). 0.75/0.85 sits between Disrupt's
//     tier and "no effect": forward firepower is felt as weakened in the
//     last minute, but a player who has invested in turrets still benefits
//     from them. Composes multiplicatively with Disrupt and Fortify on the
//     cooldown factor (Disrupt × Storm: 0.6 × 0.75 = 0.45 rate; Storm ×
//     Fortify: 0.75 × 1.25 = 0.9375 rate; Disrupt × Storm × Fortify: 0.6
//     × 0.75 × 1.25 = 0.5625 rate). The storm is the OUTERMOST multiplier
//     applied last — same composition pattern Slice 7 introduced for
//     Fortify on top of Disrupt.
//   - `SIGNAL_STORM_CAPTURE_SPEED_MULT = 1.5`
//     Tuned so a contested relay flips in ~7 s instead of ~10 s — fast
//     enough that a final commit can change dominance, slow enough that
//     the defender still has reaction time. Engineer's existing 50%
//     capture-speed bonus composes multiplicatively (Engineer in storm =
//     1.5 × 1.5 = 2.25× — a clear advantage for the player who has
//     Engineer alive at 7:00, which is correct: the player who built and
//     held an Engineer all match deserves the late-game payoff).
//   - `SIGNAL_STORM_START_SEC = 300` (5:00)
//     V2 Tempo patch (2026-05-24) — moved from 7:00 → 5:00 in lockstep
//     with match length 8:00 → 6:00 (matchConfig.ts). The storm window
//     is still the final 60 s of regulation, just on a shorter clock.
//     Matches the §8 pacing table's "Final pressure" window. The storm
//     IS the final pressure phase — until this slice, that table cell
//     described player intent, not a sim-side mechanic.
//
// §17 anti-turtling gate calibration:
//   This slice's pre/post comparison watches `relayFlipsDuringStorm`
//   (a NEW telemetry counter — flips that landed during the storm
//   window) as the load-bearing metric, not aggregate
//   `averageRelayFlipsPerMatch`. The storm is opt-in for whoever wants
//   to push, not a universal flip-rate amplifier. The aggregate flip
//   rate must stay ≥ 8.5 (post-Slice-7 baseline 8.97 with 5% headroom);
//   `relayFlipsDuringStorm` must be ≥ 0.8 per match (the storm must
//   produce at least one flip in the 60 s window across the bot-vs-bot
//   population). If either fails, retune CAPTURE first (range
//   1.3–1.7); turret multipliers affect the deathball-prevention
//   dynamic which is harder to re-tune later.
//
// Don't tune these without a corresponding playtest verification: at
// minimum a 30-match `playtest-direct.mjs 30` showing both gates pass.
export const SIGNAL_STORM_START_SEC = 300;
export const SIGNAL_STORM_TURRET_FIRE_RATE_MULT = 0.75;
export const SIGNAL_STORM_TURRET_RANGE_MULT = 0.85;
export const SIGNAL_STORM_CAPTURE_SPEED_MULT = 1.5;
